import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { errorMessage } from '@/lib/utils'
import {
  PLATFORM_LABEL,
  PLATFORMS,
  rollbackPatchInputSchema,
  useRollbackPatch,
  useRollforwardPatch,
  useShorebirdReleasePatches,
  useShorebirdReleases,
  type PatchPolicyItem,
  type Platform,
  type PromotePatchApiResult,
  type RollforwardResult,
  type ShorebirdReleasePatch,
} from '@/features/app-patch-policy/api'
import { useLocale } from '@/features/app-patch-policy/locale'

type RollbackOutcome = {
  platform: Platform
  result?: PromotePatchApiResult
  error?: string
}

type RollforwardOutcome = {
  platform: Platform
  result?: RollforwardResult
  error?: string
}

// already_promoted alone doesn't mean "nothing happened" — rolling back can leave the target
// patch's own channel untouched while still marking one or more newer patches rolled back to
// make it actually win again (see service.RollbackToPatch in cwc-admin-v1). Only genuinely
// nothing-to-do when both are true/empty at once.
function rollbackOutcomeText(outcome: RollbackOutcome): string {
  if (outcome.error) return outcome.error
  const result = outcome.result
  if (!result) return ''
  const rolledBack = result.rolled_back_patch_numbers
  if (result.already_promoted && rolledBack.length === 0) {
    return `patch ${result.patch_number} was already on stable — nothing to do.`
  }
  const rolledBackSuffix =
    rolledBack.length > 0 ? ` — rolled back patch${rolledBack.length > 1 ? 'es' : ''} ${rolledBack.join(', ')}` : ''
  const promotedSuffix =
    !result.already_promoted && result.previous_channel ? ` (was previously on ${result.previous_channel})` : ''
  return `stable now on patch ${result.patch_number}${rolledBackSuffix}${promotedSuffix}.`
}

interface RollbackTarget {
  release_version: string
  patch_number: number
}

const EMPTY_ROLLBACK_TARGET: RollbackTarget = { release_version: '', patch_number: 0 }

/**
 * "Emergency rollback" tab: moves stable back to an earlier patch after something already live
 * turns out to have a problem, or reactivates one that was rolled back by mistake. Both platforms
 * can be submitted at once, each picking its own independent target patch.
 */
export function RollbackSection({ data }: { data: PatchPolicyItem[] }) {
  const { t } = useLocale()
  const [selected, setSelected] = useState<Platform[]>([])
  const [targets, setTargets] = useState<Record<Platform, RollbackTarget>>({
    ios: EMPTY_ROLLBACK_TARGET,
    android: EMPTY_ROLLBACK_TARGET,
  })
  const [ack, setAck] = useState(false)
  const [outcomes, setOutcomes] = useState<RollbackOutcome[] | null>(null)

  // Fetched for both platforms unconditionally (hooks can't be called in a loop) — needed so a
  // platform with zero releases can be disabled before it's ever checked, same as promote-panel.
  const iosReleases = useShorebirdReleases('ios')
  const androidReleases = useShorebirdReleases('android')
  const releasesByPlatform: Record<Platform, typeof iosReleases> = { ios: iosReleases, android: androidReleases }
  function noReleasesFor(platform: Platform) {
    const query = releasesByPlatform[platform]
    return query.isSuccess && (query.data?.length ?? 0) === 0
  }

  // Derived from each target's own release_version/patch_number rather than tracked in separate
  // state: staff can type these fields directly instead of using the pickers, and separate state
  // desyncs from that free-text edit — the patch Select then silently keeps offering the previously
  // picked release's patches while the form shows a different release_version (or worse, picking
  // from it writes back a patch_number that belongs to the wrong release).
  const releaseIdByPlatform: Record<Platform, number | null> = {
    ios: iosReleases.data?.find((r) => r.version === targets.ios.release_version)?.id ?? null,
    android: androidReleases.data?.find((r) => r.version === targets.android.release_version)?.id ?? null,
  }

  const iosPatches = useShorebirdReleasePatches(releaseIdByPlatform.ios, 'ios')
  const androidPatches = useShorebirdReleasePatches(releaseIdByPlatform.android, 'android')
  const patchesByPlatform: Record<Platform, typeof iosPatches> = { ios: iosPatches, android: androidPatches }

  // Only ever-promoted patches (channel === 'stable') are valid rollback targets — the single
  // source both the Select's options and its matched value are built from, so they can't drift.
  const stablePatchesByPlatform: Record<Platform, ShorebirdReleasePatch[]> = {
    ios: iosPatches.data?.filter((patch) => patch.channel === 'stable') ?? [],
    android: androidPatches.data?.filter((patch) => patch.channel === 'stable') ?? [],
  }

  const patchIdByPlatform: Record<Platform, number | null> = {
    ios: stablePatchesByPlatform.ios.find((p) => p.number === targets.ios.patch_number)?.id ?? null,
    android: stablePatchesByPlatform.android.find((p) => p.number === targets.android.patch_number)?.id ?? null,
  }

  const rollbackIos = useRollbackPatch()
  const rollbackAndroid = useRollbackPatch()
  const mutationByPlatform: Record<Platform, typeof rollbackIos> = { ios: rollbackIos, android: rollbackAndroid }

  const rollforwardIos = useRollforwardPatch()
  const rollforwardAndroid = useRollforwardPatch()
  const rollforwardByPlatform: Record<Platform, typeof rollforwardIos> = { ios: rollforwardIos, android: rollforwardAndroid }
  const [rollforwardOutcomes, setRollforwardOutcomes] = useState<RollforwardOutcome[] | null>(null)

  function toggle(platform: Platform, checked: boolean) {
    setSelected((prev) => (checked ? [...prev, platform] : prev.filter((p) => p !== platform)))
    setOutcomes(null)
    setRollforwardOutcomes(null)
    setAck(false)
    if (!checked) {
      setTargets((prev) => ({ ...prev, [platform]: EMPTY_ROLLBACK_TARGET }))
    }
  }

  // Drop a platform the moment it's confirmed to have zero releases, even if it was checked
  // before that was known.
  useEffect(() => {
    const stale = selected.filter(noReleasesFor)
    if (stale.length === 0) return
    setSelected((prev) => prev.filter((p) => !stale.includes(p)))
    setTargets((prev) => {
      const next = { ...prev }
      for (const p of stale) next[p] = EMPTY_ROLLBACK_TARGET
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iosReleases.isSuccess, iosReleases.data, androidReleases.isSuccess, androidReleases.data])

  // Any target change resets the acknowledgement, so a stale check can't authorize a patch the
  // operator never reviewed.
  function updateTarget(platform: Platform, patch: Partial<RollbackTarget>) {
    setTargets((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }))
    setAck(false)
  }

  function pickRelease(platform: Platform, releaseId: number) {
    const release = releasesByPlatform[platform].data?.find((r) => r.id === releaseId)
    updateTarget(platform, { release_version: release?.version ?? '', patch_number: 0 })
  }

  function pickPatch(platform: Platform, patchId: number) {
    const patch = patchesByPlatform[platform].data?.find((p) => p.id === patchId)
    updateTarget(platform, { patch_number: patch?.number ?? 0 })
  }

  const validTargets =
    selected.length > 0 &&
    selected.every((p) => rollbackPatchInputSchema.safeParse({ platform: p, ...targets[p] }).success)
  const anyPending = selected.some((p) => mutationByPlatform[p].isPending)

  async function onSubmit() {
    setOutcomes(null)
    const results = await Promise.all(
      selected.map(async (platform): Promise<RollbackOutcome> => {
        const target = targets[platform]
        try {
          const result = await mutationByPlatform[platform].mutateAsync({
            platform,
            release_version: target.release_version,
            patch_number: target.patch_number,
          })
          return { platform, result }
        } catch (err) {
          return { platform, error: errorMessage(err, 'Rollback failed.') }
        }
      }),
    )
    setOutcomes(results)
    if (!results.some((r) => r.error)) {
      setSelected([])
      setTargets({ ios: EMPTY_ROLLBACK_TARGET, android: EMPTY_ROLLBACK_TARGET })
      setAck(false)
    }
  }

  const anyRollforwardPending = selected.some((p) => rollforwardByPlatform[p].isPending)

  async function onRollforward() {
    setRollforwardOutcomes(null)
    const results = await Promise.all(
      selected.map(async (platform): Promise<RollforwardOutcome> => {
        const target = targets[platform]
        try {
          const result = await rollforwardByPlatform[platform].mutateAsync({
            platform,
            release_version: target.release_version,
            patch_number: target.patch_number,
          })
          return { platform, result }
        } catch (err) {
          return { platform, error: errorMessage(err, 'Roll forward failed.') }
        }
      }),
    )
    setRollforwardOutcomes(results)
    if (!results.some((r) => r.error)) {
      setSelected([])
      setTargets({ ios: EMPTY_ROLLBACK_TARGET, android: EMPTY_ROLLBACK_TARGET })
      setAck(false)
    }
  }

  // Rollback and rollforward are opposite actions for the same target — showing both invites
  // clicking the wrong one. Once every selected platform's target patch is known and agrees on
  // being rolled back (or not), only the button that applies is shown; unknown or mixed falls
  // back to showing both.
  const targetPatchStates = selected.map((platform) =>
    patchesByPlatform[platform].data?.find((p) => p.number === targets[platform].patch_number),
  )
  const allTargetsKnown = targetPatchStates.every((p): p is ShorebirdReleasePatch => p != null)
  const allTargetsRolledBack = allTargetsKnown && targetPatchStates.every((p) => p.is_rolled_back)
  const allTargetsActive = allTargetsKnown && targetPatchStates.every((p) => !p.is_rolled_back)
  const showRollbackButton = !allTargetsRolledBack
  const showRollforwardButton = !allTargetsActive

  return (
    <Card className="border-destructive/40 bg-destructive/[0.03]">
      <CardTitle>{t('rollback.title')}</CardTitle>
      <CardDescription className="mt-1">{t('rollback.description')}</CardDescription>

      <div className="mt-5 space-y-4">
        <div className="max-w-xs space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <Label>{t('rollback.platformLabel')}</Label>
            <span className="text-[11px] text-faint">{t('rollback.multiSelectHint')}</span>
          </div>
          <div className="flex flex-col gap-2">
            {PLATFORMS.map((p) => {
              const disabled = noReleasesFor(p)
              return (
                <label key={p} className={`flex items-center gap-2 text-sm ${disabled ? 'text-muted-foreground' : ''}`}>
                  <Checkbox
                    checked={selected.includes(p)}
                    disabled={disabled}
                    onCheckedChange={(value) => toggle(p, Boolean(value))}
                  />
                  {PLATFORM_LABEL[p]}
                  {disabled && <span className="text-xs">{t('promote.noReleasesYet')}</span>}
                </label>
              )
            })}
          </div>
        </div>

        {selected.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('rollback.pickPlatformFirst')}</p>
        )}

        {selected.map((platform) => {
          const item = data.find((row) => row.platform === platform)
          const releasesQuery = releasesByPlatform[platform]
          const patchesQuery = patchesByPlatform[platform]
          const target = targets[platform]
          const stablePatches = stablePatchesByPlatform[platform]
          const typedPatch = patchesQuery.data?.find((p) => p.number === target.patch_number)
          return (
            <div key={platform} className="space-y-2 rounded-md border border-destructive/30 bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{PLATFORM_LABEL[platform]}</span>
                <span className="text-xs text-muted-foreground">
                  {t('rollback.currentPrefix')}{' '}
                  {item?.latest_patch_number != null ? `patch ${item.latest_patch_number}` : t('platform.notSyncedYet')}
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor={`rollback-${platform}-release`} className="text-xs">
                    {t('rollback.releaseVersionLabel')}
                  </Label>
                  <Select
                    value={releaseIdByPlatform[platform] !== null ? String(releaseIdByPlatform[platform]) : undefined}
                    onValueChange={(value) => pickRelease(platform, Number(value))}
                    disabled={releasesQuery.isPending || releasesQuery.isError}
                  >
                    <SelectTrigger id={`rollback-${platform}-release`}>
                      <SelectValue
                        placeholder={
                          releasesQuery.isPending ? t('promote.pickerLoadingReleases') : t('promote.pickerPromptRelease')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {releasesQuery.data?.map((release) => (
                        <SelectItem key={release.id} value={String(release.id)}>
                          {release.version} ({release.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {releasesQuery.isError && <p className="text-xs text-warning">{t('promote.releasesLoadError')}</p>}
                  <Input
                    placeholder={t('promote.releaseVersionPlaceholder')}
                    value={target.release_version}
                    onChange={(event) => updateTarget(platform, { release_version: event.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`rollback-${platform}-patch`} className="text-xs">
                    {t('rollback.patchNumberLabel')}
                  </Label>
                  {releaseIdByPlatform[platform] !== null ? (
                    <Select
                      value={patchIdByPlatform[platform] !== null ? String(patchIdByPlatform[platform]) : undefined}
                      onValueChange={(value) => pickPatch(platform, Number(value))}
                      disabled={patchesQuery.isPending || patchesQuery.isError}
                    >
                      <SelectTrigger id={`rollback-${platform}-patch`}>
                        <SelectValue
                          placeholder={
                            patchesQuery.isPending ? t('promote.pickerLoadingPatches') : t('rollback.pickerPromptPatch')
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Only ever-promoted patches (channel === 'stable', including rolled-back ones —
                            that tag isn't removed by a rollback) are valid targets; plain "staging" patches
                            belong in the Promote panel instead. */}
                        {stablePatches.map((patch) => (
                          <SelectItem key={patch.id} value={String(patch.id)}>
                            Patch {patch.number} {patch.is_rolled_back ? '(rolled back)' : '(active)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('rollback.pickReleaseFirst')}</p>
                  )}
                  {patchesQuery.isError && <p className="text-xs text-warning">{t('promote.patchesLoadError')}</p>}
                  {!patchesQuery.isPending && !patchesQuery.isError && releaseIdByPlatform[platform] !== null && stablePatches.length === 0 && (
                    <p className="text-xs text-warning">{t('rollback.noStablePatches')}</p>
                  )}
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={target.patch_number || ''}
                    onChange={(event) =>
                      updateTarget(platform, {
                        patch_number: event.target.value === '' ? 0 : Number(event.target.value),
                      })
                    }
                  />
                  {/* Heads-up only: RollbackToPatch is the real gate, since this list can be stale. */}
                  {typedPatch && typedPatch.channel !== 'stable' && (
                    <p className="text-xs text-warning">
                      Patch {typedPatch.number} was only ever published to &quot;{typedPatch.channel}&quot;, not stable —
                      rollback will reject it. Pick a patch from the dropdown above, or use Promote (step 2) if
                      you mean to ship it.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {selected.length > 0 && (
          <>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={ack} onCheckedChange={(value) => setAck(Boolean(value))} />
              <span className="space-y-0.5">
                {selected.map((platform) => (
                  <span key={platform} className="block">
                    {t(showRollforwardButton && !showRollbackButton ? 'rollback.confirmRollforward' : 'rollback.confirm', {
                      platform: PLATFORM_LABEL[platform],
                      patch: String(targets[platform].patch_number || '—'),
                    })}
                  </span>
                ))}
              </span>
            </label>

            <div className="flex flex-wrap items-center gap-3">
              {showRollbackButton && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={!ack || !validTargets || anyPending}
                  onClick={() => void onSubmit()}
                >
                  {anyPending ? t('rollback.submitting') : t('rollback.submit')}
                </Button>
              )}
              {showRollforwardButton && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!ack || !validTargets || anyRollforwardPending}
                  onClick={() => void onRollforward()}
                >
                  {anyRollforwardPending ? t('rollback.rollforwardSubmitting') : t('rollback.rollforwardSubmit')}
                </Button>
              )}
            </div>
          </>
        )}

        {outcomes && (
          <div className="space-y-1">
            {outcomes.map((outcome) => (
              <div key={outcome.platform} className="space-y-1">
                <p className={`text-xs ${outcome.error ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {PLATFORM_LABEL[outcome.platform]}: {rollbackOutcomeText(outcome)}
                </p>
                {outcome.result?.policy_adjusted && (
                  <p className="text-xs text-warning">
                    The mandatory floor for {PLATFORM_LABEL[outcome.result.policy_adjusted.platform]}{' '}
                    {outcome.result.policy_adjusted.release_version} was above patch {outcome.result.patch_number}{' '}
                    and would have been unreachable — automatically lowered to match.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {rollforwardOutcomes && (
          <div className="space-y-1">
            {rollforwardOutcomes.map((outcome) => (
              <p key={outcome.platform} className={`text-xs ${outcome.error ? 'text-destructive' : 'text-muted-foreground'}`}>
                {PLATFORM_LABEL[outcome.platform]}:{' '}
                {outcome.error
                  ? outcome.error
                  : outcome.result?.already_active
                    ? `patch ${outcome.result.patch_number} wasn't rolled back — nothing to do.`
                    : `patch ${outcome.result?.patch_number} rolled forward — reactivated.`}
              </p>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
